/**
 * F60 — Ops logs & API health client (super_admin only, `ops_logs:read`).
 *
 *   GET /admin/ops/logs/info     → getInfo     { data: OpsLogsInfo }
 *   GET /admin/ops/logs/recent   → getRecent   { data: OpsLogEvent[] }
 *   GET /admin/ops/logs          → search      { data: OpsLogEvent[], nextCursor, meta }
 *   GET /admin/ops/logs/stats    → getStats    { data: OpsLogsStats, meta }
 *   GET /admin/ops/logs/latency  → getLatency  { data: OpsLatencyReport, meta }
 *   GET /admin/ops/logs/export   → exportPage  { data: OpsLogsExportPage, meta }
 *
 * Single-value query params (`buildQuery`). The vitest config aliases this
 * module to `opsLogs.fixture` during tests; the real request shapes are pinned
 * by test/client-contracts-ops-logs.test.ts.
 */
import type {
  OpsLatencyReport,
  OpsLogEvent,
  OpsLogLevel,
  OpsLogsEventsPage,
  OpsLogsExportPage,
  OpsLogsFilters,
  OpsLogsInfo,
  OpsLogsScanMeta,
  OpsLogsStats,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

interface DataEnvelope<T> {
  readonly data: T;
}

interface ScannedEnvelope<T> {
  readonly data: T;
  readonly meta: OpsLogsScanMeta;
}

export interface OpsRange {
  /** ISO-8601. */
  readonly from: string;
  readonly to: string;
}

export interface OpsSearchOpts extends OpsRange, OpsLogsFilters {
  readonly limit?: number;
  readonly cursor?: string;
}

export interface OpsExportOpts extends OpsRange, OpsLogsFilters {
  readonly cursor?: string;
}

export interface OpsRecentOpts {
  readonly since?: string;
  readonly limit?: number;
  readonly minLevel?: OpsLogLevel;
}

function filterParams(f: OpsLogsFilters): Record<string, string | undefined> {
  return {
    minLevel: f.minLevel,
    method: f.method,
    route: f.route,
    status: f.status,
    requestId: f.requestId,
    userId: f.userId,
    adminId: f.adminId,
    text: f.text,
  };
}

export const adminOpsLogsApi = {
  getInfo: async (): Promise<OpsLogsInfo> => {
    const body = await adminFetch<DataEnvelope<OpsLogsInfo>>('/admin/ops/logs/info');
    return body.data;
  },

  getRecent: async (opts: OpsRecentOpts = {}): Promise<readonly OpsLogEvent[]> => {
    const body = await adminFetch<DataEnvelope<readonly OpsLogEvent[]>>(
      `/admin/ops/logs/recent${buildQuery({ since: opts.since, limit: opts.limit, minLevel: opts.minLevel })}`,
    );
    return body.data;
  },

  search: async (opts: OpsSearchOpts): Promise<OpsLogsEventsPage> =>
    adminFetch<OpsLogsEventsPage>(
      `/admin/ops/logs${buildQuery({
        from: opts.from,
        to: opts.to,
        ...filterParams(opts),
        limit: opts.limit,
        cursor: opts.cursor,
      })}`,
    ),

  getStats: async (range: OpsRange): Promise<ScannedEnvelope<OpsLogsStats>> =>
    adminFetch<ScannedEnvelope<OpsLogsStats>>(
      `/admin/ops/logs/stats${buildQuery({ from: range.from, to: range.to })}`,
    ),

  getLatency: async (range: OpsRange): Promise<ScannedEnvelope<OpsLatencyReport>> =>
    adminFetch<ScannedEnvelope<OpsLatencyReport>>(
      `/admin/ops/logs/latency${buildQuery({ from: range.from, to: range.to })}`,
    ),

  exportPage: async (opts: OpsExportOpts): Promise<OpsLogsExportPage> => {
    const body = await adminFetch<ScannedEnvelope<OpsLogsExportPage>>(
      `/admin/ops/logs/export${buildQuery({
        from: opts.from,
        to: opts.to,
        ...filterParams(opts),
        cursor: opts.cursor,
      })}`,
    );
    return body.data;
  },
};
